import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { AppConfigService } from 'app/core/config/app-config.service';

export interface PendingStep {
  student_course_step_id: number;
  step_title: string;
}

export interface NextCourse {
  course_id: number;
  title: string;
  description?: string;
  module?: string;
  sort_order?: number;
  is_mandatory?: boolean;
  due_by_days?: number;
  is_nachschulung?: boolean;
  /** Whether the student is eligible to be enrolled in this course */
  eligible?: boolean;
  /** Reason code returned by EligibilityService: 'free_course' | 'payment_bypass' | 'already_enrolled' | 'not_paid' | 'payment_check_disabled' */
  eligibility_reason?: string;
  /** What the UI should show when the student is not eligible: 'teaser' | 'message' | 'redirect' */
  unenrolled_mode?: 'teaser' | 'message' | 'redirect';
  /** Company-level webshop URL shown in the teaser/message states */
  webshop_url?: string | null;
  /** True when the student is already enrolled in this course (fallback path) */
  already_enrolled?: boolean;
  /** The student_course_id if the student is already enrolled (fallback path) */
  student_course_id?: number;
}

export interface NextCourseResponse {
  /** The next course to enroll in (null if none) */
  data: NextCourse | null;
  /** Whether the student is allowed to progress now */
  progression_eligible: boolean;
  /** Reason why progression is blocked:
   *   'pending_revision'           — status=3 (brown) steps exist; student must act first
   *   'pending_supervisor_review'  — status=2 (orange) steps exist and STUDENT_ADVANCE_WITH_PENDING=false
   *   'supervisor_approval_required' — SUPERVISOR_START_MANDATORY=true and not all steps are status=4
   */
  progression_reason?: 'pending_revision' | 'pending_supervisor_review' | 'supervisor_approval_required' | null;
  /** Steps at status=3 (brown) — student must respond before progressing */
  pending_revision_steps: PendingStep[];
  /** Steps at status=2 (orange) — waiting for supervisor review */
  orange_steps: PendingStep[];
  /** Whether the company requires supervisor to manually start next course */
  supervisor_start_mandatory: boolean;
  /** Whether students may advance to next course while orange (status=2) steps are pending */
  student_advance_with_pending: boolean;
}

export interface StartNextCourseResult {
  student_course_id?: number;
  course_id?: number;
  course_title?: string;
  already_enrolled?: boolean;
  /** Set to 'not_eligible' when the server returns 403 due to payment gate */
  error?: string;
  unenrolled_mode?: string;
  webshop_url?: string | null;
}

export interface RatingResult {
  student_course_id: number;
  rating_tier: number;
  nachschulung_triggered: boolean;
  full_retake: boolean;
  auto_trigger_threshold: number;
  retake_threshold: number;
}

export interface CompanyConfig {
  key: string;
  value: string;
  company_id: number;
}

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': '*' }),
};

@Injectable({ providedIn: 'root' })
export class CompletionFlowService {
  constructor(private httpClient: HttpClient, private appConfig: AppConfigService) {}

  getNextCourse(studentId: number, studentCourseId?: number | null): Observable<NextCourseResponse> {
    const url = `${this.appConfig.get<string>('serverUrl', '')}/v1/students/${studentId}/next-course${studentCourseId ? '?student_course_id=' + studentCourseId : ''}`;
    return this.httpClient
      .get<any>(url, httpOptions)
      .pipe(
        map(r => ({
          data:                        r.data ?? null,
          progression_eligible:        r.progression_eligible ?? true,
          progression_reason:          r.progression_reason   ?? null,
          pending_revision_steps:      r.pending_revision_steps    ?? [],
          orange_steps:                r.orange_steps              ?? [],
          supervisor_start_mandatory:  r.supervisor_start_mandatory  ?? true,
          student_advance_with_pending: r.student_advance_with_pending ?? false,
        } as NextCourseResponse)),
        catchError(this.handleError)
      );
  }

  startNextCourse(studentId: number, courseId?: number): Observable<StartNextCourseResult> {
    const body = courseId ? { course_id: courseId } : {};
    return this.httpClient
      .post<any>(`${this.appConfig.get<string>('serverUrl', '')}/v1/students/${studentId}/start-next-course`, JSON.stringify(body), httpOptions)
      .pipe(
        map(r => r.data as StartNextCourseResult),
        catchError((httpError) => {
          // 403 from eligibility gate — surface the structured error to the component
          const body = httpError?.error;
          if (httpError?.status === 403 && body?.error === 'not_eligible') {
            const result: StartNextCourseResult = {
              error: 'not_eligible',
              unenrolled_mode: body.unenrolled_mode,
              webshop_url: body.webshop_url,
            };
            // Return as a resolved value (not an error) so the subscriber can branch on result.error
            return [result];
          }
          return this.handleError(httpError);
        })
      );
  }

  rateStudentCourse(studentCourseId: number, ratingTier: number): Observable<RatingResult> {
    return this.httpClient
      .post<any>(`${this.appConfig.get<string>('serverUrl', '')}/v1/student-courses/${studentCourseId}/rate`, JSON.stringify({ rating_tier: ratingTier }), httpOptions)
      .pipe(map(r => r.data), catchError(this.handleError));
  }

  getCompanyConfig(key: string, companyId?: number): Observable<CompanyConfig> {
    const url = `${this.appConfig.get<string>('serverUrl', '')}/v1/company-config/${key}${companyId ? '?company_id=' + companyId : ''}`;
    return this.httpClient
      .get<any>(url, httpOptions)
      .pipe(map(r => r.data), catchError(this.handleError));
  }

  setCompanyConfig(key: string, value: string, companyId?: number): Observable<CompanyConfig> {
    const body: { value: string; company_id?: number } = { value };
    if (companyId) {
      body.company_id = companyId;
    }
    return this.httpClient
      .put<any>(`${this.appConfig.get<string>('serverUrl', '')}/v1/company-config/${key}`, JSON.stringify(body), httpOptions)
      .pipe(map(r => r.data), catchError(this.handleError));
  }

  private handleError(error: any) {
    const msg = error?.error?.error || error?.message || 'Unknown error';
    return throwError(() => msg);
  }
}
